/* eslint-disable sort-imports */
/**
 * Upload review, job-order PDF — the header the parser read (job order,
 * hours, vehicle) and the binding panel: which product and work centre the
 * standard belongs to.
 */
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import { Box, Chip, Divider, FormControl, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Typography } from "@mui/material";
import React, { useCallback } from "react";

import { JobOrderParseResult } from "../../../../services/labor-standards-api";
import { Product } from "../../../../services/products-api";
import { MonoText, planner,SectionLabel } from "../../planner-ui";
import { ProductMenuEmpty, ProductMenuSearch, useProductMenuSearch } from "../../planner-ui/product-menu-search";
import { formatHours } from "../utils/recipe-mapping";
import { chipMonoSx, chipSx, fieldSx, menuItemSx, panelSx, statusChipSx } from "./upload-styles";

export const PdfSummary: React.FC<{ pdfResult: JobOrderParseResult }> = ({ pdfResult }) => {
	const vehicle = pdfResult.vehicle;
	const vehicleLabel = [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(" ");
	return (
		<Paper elevation={0} sx={{ ...panelSx, mb: 2 }}>
			<Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
				<Box>
					<SectionLabel>Job order</SectionLabel>
					<Box display="flex" alignItems="baseline" gap={1.5} sx={{ mt: 0.5 }} flexWrap="wrap">
						<Typography sx={{ ...planner.type.pageTitle, fontFamily: planner.fontMono, fontSize: 22, color: planner.navy }}>
							{pdfResult.sourceReference || "—"}
						</Typography>
						{pdfResult.revision && <Chip label={`Rev ${pdfResult.revision}`} size="small" sx={chipMonoSx} />}
						{pdfResult.customer && <Chip label={pdfResult.customer} size="small" sx={chipSx} />}
					</Box>
					{pdfResult.description && (
						<Typography sx={{ ...planner.type.body, fontFamily: planner.fontUi, color: planner.inkSecondary, mt: 0.75 }}>
							{pdfResult.description}
						</Typography>
					)}
				</Box>

				{/* Parsed hours + reconciliation */}
				<Box sx={{ textAlign: "right" }}>
					<SectionLabel sx={{ textAlign: "right" }}>Parsed hours</SectionLabel>
					<Typography sx={{ ...planner.type.stat, fontFamily: planner.fontMono, fontSize: 22, color: planner.accentDeep, mt: 0.25 }}>
						{formatHours(pdfResult.parsedTotalHours)}
					</Typography>
					{pdfResult.totalsReconcile ? (
						<Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5} sx={{ mt: 0.25 }}>
							<CheckCircleIcon sx={{ fontSize: 14, color: planner.staffed }} />
							<Typography sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.staffed }}>matches document</Typography>
						</Box>
					) : (
						<Typography sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.partial, mt: 0.25 }}>
							does not match document
						</Typography>
					)}
				</Box>
			</Box>

			{/* Vehicle */}
			{(vehicle?.vin || vehicle?.unitNo || vehicleLabel) && (
				<>
					<Divider sx={{ my: 1.5, borderColor: planner.hairline }} />
					<Box display="flex" alignItems="center" gap={2.5} flexWrap="wrap">
						{vehicleLabel && (
							<Typography sx={{ ...planner.type.bodyStrong, fontFamily: planner.fontUi, color: planner.ink }}>{vehicleLabel}</Typography>
						)}
						{vehicle?.vin && (
							<Box display="flex" alignItems="baseline" gap={0.75}>
								<Typography sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.inkMuted }}>VIN</Typography>
								<MonoText sx={{ color: planner.ink, fontSize: 13, letterSpacing: "0.04em" }}>{vehicle.vin}</MonoText>
							</Box>
						)}
						{vehicle?.unitNo && (
							<Box display="flex" alignItems="baseline" gap={0.75}>
								<Typography sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.inkMuted }}>Unit</Typography>
								<MonoText sx={{ color: planner.ink, fontSize: 13 }}>{vehicle.unitNo}</MonoText>
							</Box>
						)}
						{pdfResult.startDate && (
							<Box display="flex" alignItems="baseline" gap={0.75}>
								<Typography sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.inkMuted }}>Start</Typography>
								<MonoText sx={{ color: planner.ink, fontSize: 13 }}>{pdfResult.startDate}</MonoText>
							</Box>
						)}
					</Box>
				</>
			)}
		</Paper>
	);
};

export const PdfBinding: React.FC<{
	pdfResult: JobOrderParseResult;
	products: Product[];
	productId: number | "";
	onProductChange: (productId: number | "") => void;
	workcenter: string;
	workcenterOptions: string[];
	onWorkcenterChange: (workcenter: string) => void;
}> = ({ pdfResult, products, productId, onProductChange, workcenter, workcenterOptions, onWorkcenterChange }) => {
	const handleProduct = useCallback(
		(event: SelectChangeEvent<number | "">) => onProductChange(event.target.value === "" ? "" : Number(event.target.value)),
		[onProductChange]
	);
	const handleWorkcenter = useCallback((event: SelectChangeEvent<string>) => onWorkcenterChange(event.target.value), [onWorkcenterChange]);
	const productSearch = useProductMenuSearch(products, productId);
	return (
		<Paper elevation={0} sx={{ ...panelSx, mb: 2 }}>
			<SectionLabel>Where this standard belongs</SectionLabel>
			<Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap" sx={{ mt: 1.5 }}>
				<Box sx={{ minWidth: 280, flex: "1 1 280px" }}>
					{pdfResult.matchedProductId ? (
						<Box>
							<Typography sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.inkMuted, mb: 0.5 }}>Product</Typography>
							<Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
								<Typography sx={{ ...planner.type.bodyStrong, fontFamily: planner.fontUi, color: planner.ink }}>
									{pdfResult.matchedProductName || `Product ${pdfResult.matchedProductId}`}
								</Typography>
								{pdfResult.specCode && <Chip label={pdfResult.specCode} size="small" sx={chipMonoSx} />}
								<Chip label="Matched" size="small" sx={statusChipSx(planner.staffed, planner.staffedSoft)} />
							</Box>
						</Box>
					) : (
						<>
							<FormControl fullWidth size="small" sx={fieldSx}>
								<InputLabel id="review-product-label">Product</InputLabel>
								<Select labelId="review-product-label" label="Product" value={productId} onChange={handleProduct} {...productSearch.selectProps}>
									<ProductMenuSearch query={productSearch.query} onQueryChange={productSearch.setQuery} />
									{productSearch.visible.map((product) => (
										<MenuItem key={product.id} value={product.id} sx={menuItemSx}>
											{product.itemNumber} — {product.name}
										</MenuItem>
									))}
									{productSearch.isEmpty && <ProductMenuEmpty />}
								</Select>
							</FormControl>
							<Typography sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.inkMuted, mt: 0.75 }}>
								{pdfResult.specCode ? (
									<>
										{"Spec code "}
										<Box component="span" sx={{ fontFamily: planner.fontMono }}>
											{pdfResult.specCode}
										</Box>
										{" did not match any product, so pick the one this job order builds."}
									</>
								) : (
									"The document carries no spec code, so pick the product this job order builds."
								)}
							</Typography>
						</>
					)}
				</Box>

				<Box sx={{ minWidth: 260, flex: "1 1 260px" }}>
					<FormControl fullWidth size="small" sx={fieldSx} required>
						<InputLabel id="review-workcenter-label">Work centre</InputLabel>
						<Select labelId="review-workcenter-label" label="Work centre" value={workcenter} onChange={handleWorkcenter}>
							{workcenterOptions.map((wc) => (
								<MenuItem key={wc} value={wc} sx={menuItemSx}>
									{wc}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Typography
						sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: workcenter ? planner.inkMuted : planner.partial, mt: 0.75 }}
					>
						A labor standard is per product and work centre — required before saving.
					</Typography>
				</Box>
			</Box>
		</Paper>
	);
};
